import {
    ALLOWED,
    BAGGAGE_BENEFIT_KEYS,
    CARRY_ON,
    CHECK_IN,
    ELIGIBLE_BENEFITS,
    NOT_ALLOWED
} from '@/src/constants/itinerary/fareFamily'
import { ProcessedBenefit } from '@/src/types/fareFamilyInfo'
import { FareBenefit, PaxWiseBaggage } from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

interface UseBenefitProcessorProps {
    locale: string
}

const useBenefitProcessor = ({ locale }: UseBenefitProcessorProps) => {
    const t = useTranslations('common')

    const getBaggageBenefitDisplayBenefit = useCallback(
        (baggageData: PaxWiseBaggage): { type: typeof ALLOWED | typeof NOT_ALLOWED; value: string } => {
            const { measurements, baggageInfoText = '' } = baggageData || {}
            if (isEmpty(measurements)) {
                return { type: NOT_ALLOWED, value: 'Not Allowed' }
            }

            const weightObj = measurements.find(measurement => measurement?.type === 'Weight')

            if (isEmpty(weightObj?.value) || weightObj?.value == '0') {
                return { type: NOT_ALLOWED, value: 'Not Allowed' }
            }

            if (isEmpty(weightObj)) {
                return { type: ALLOWED, value: baggageInfoText || '' }
            }

            if (weightObj && baggageInfoText) {
                return {
                    type: ALLOWED,
                    value: `${weightObj.value} ${weightObj.unit} ${baggageInfoText}`
                }
            }

            return { type: NOT_ALLOWED, value: 'Not Allowed' }
        },
        []
    )

    const processBenefits = useCallback(
        (fareBenefits: FareBenefit[]): ProcessedBenefit[] => {
            if (!fareBenefits || !Array.isArray(fareBenefits)) {
                return []
            }

            return fareBenefits.reduce((acc: ProcessedBenefit[], benefit) => {
                const { benefitKey = '', description = '', benefitDisplayName = '' } = benefit || {}

                if (!ELIGIBLE_BENEFITS.includes(benefitKey as any)) {
                    return acc
                }

                const isBaggageBenefit = BAGGAGE_BENEFIT_KEYS.includes(benefitKey as 'CARRY_ON' | 'CHECK_IN')
                let {
                    value: processedDescription,
                    type
                }: {
                    value: string
                    type: typeof ALLOWED | typeof NOT_ALLOWED
                } = isBaggageBenefit
                    ? getBaggageBenefitDisplayBenefit(benefit?.paxWiseBaggages?.ADT || {})
                    : { value: description, type: ALLOWED }

                if (benefitKey === CARRY_ON || benefitKey === CHECK_IN) {
                    if (type === NOT_ALLOWED) {
                        processedDescription = t('Not allowed')
                    } else {
                        // processedDescription = formatBaggageInfo(processedDescription, locale)
                    }
                }

                const processedBenefitDisplayName =
                    benefitKey === CARRY_ON
                        ? t('cabinBagAdult')
                        : benefitKey === CHECK_IN
                          ? t('checkInBagAdult')
                          : benefitDisplayName

                const processedBenefit: ProcessedBenefit = {
                    benefitDisplayName: processedBenefitDisplayName,
                    benefitKey,
                    description: processedDescription,
                    type
                }

                return [...acc, processedBenefit]
            }, [])
        },
        [t, getBaggageBenefitDisplayBenefit, locale]
    )

    return { processBenefits }
}

export default useBenefitProcessor
